import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';

interface College {
  id: number;
  name: string;
}

interface AssignmentResult {
  name: string;
  student_id: string;
  gender: string;
  college_name: string;
}

export default function Assign() {
  const navigate = useNavigate();
  const [seed, setSeed] = useState(42);
  const [running, setRunning] = useState(false);
  const [runId, setRunId] = useState<number | null>(null);
  const [summary, setSummary] = useState<{ [key: number]: number } | null>(null);
  const [colleges, setColleges] = useState<College[]>([]);
  const [results, setResults] = useState<AssignmentResult[]>([]);
  const [filter, setFilter] = useState<{ college_id?: number; gender?: string }>({});

  useEffect(() => {
    api.get('/api/colleges')
      .then(res => setColleges(res.data))
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const handleAssign = async () => {
    if (!window.confirm(`确认运行分配算法？随机种子：${seed}`)) return;
    setRunning(true);
    try {
      const res = await api.post(`/api/admin/assign?seed=${seed}`);
      setRunId(res.data.run_id);
      setSummary(res.data.summary);
      fetchResults();
    } catch (e) {
      alert('分配失败，请确认所有用户已完成答题');
    } finally {
      setRunning(false);
    }
  };

  const fetchResults = async () => {
    const params = new URLSearchParams();
    if (filter.college_id) params.append('college_id', String(filter.college_id));
    if (filter.gender) params.append('gender', filter.gender);
    const res = await api.get(`/api/admin/assignments?${params.toString()}`);
    setResults(res.data);
  };

  useEffect(() => {
    if (runId) fetchResults();
  }, [filter]);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">运行分配</h1>
          <button onClick={() => navigate('/admin/dashboard')}
            className="text-sm text-gray-500 hover:text-gray-700">← 返回</button>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="font-semibold text-gray-700 mb-4">分配设置</h2>
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">随机种子</label>
              <input
                type="number"
                className="border border-gray-300 rounded-lg px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                value={seed}
                onChange={e => setSeed(parseInt(e.target.value) || 42)}
              />
            </div>
            <button
              onClick={handleAssign}
              disabled={running}
              className="mt-5 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {running ? '运行中...' : '运行分配算法'}
            </button>
          </div>
        </div>

        {summary && (
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <h2 className="font-semibold text-gray-700 mb-4">分配结果概览（第 {runId} 次）</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {colleges.map(c => (
                <div key={c.id} className="bg-indigo-50 rounded-xl p-4">
                  <p className="text-sm text-indigo-600">{c.name}</p>
                  <p className="text-2xl font-bold text-indigo-800 mt-1">
                    {summary[c.id] || 0} 人
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {runId && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-gray-700">详细名单</h2>
              <div className="flex gap-3">
                <select
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                  onChange={e => setFilter({ ...filter, college_id: parseInt(e.target.value) || undefined })}
                >
                  <option value="">全部书院</option>
                  {colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                  onChange={e => setFilter({ ...filter, gender: e.target.value || undefined })}
                >
                  <option value="">全部性别</option>
                  <option value="male">男</option>
                  <option value="female">女</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-gray-600">姓名</th>
                    <th className="text-left py-2 px-3 text-gray-600">学号</th>
                    <th className="text-left py-2 px-3 text-gray-600">性别</th>
                    <th className="text-left py-2 px-3 text-gray-600">分配书院</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3">{r.name}</td>
                      <td className="py-2 px-3">{r.student_id}</td>
                      <td className="py-2 px-3">{r.gender === 'male' ? '男' : '女'}</td>
                      <td className="py-2 px-3">{r.college_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}